/* eslint-disable react-refresh/only-export-components -- Context hook must be exported alongside provider */
import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { session } from '@/utils/storage';

const STORAGE_KEY = 'research-breadcrumbs';
const MAX_TRAIL_LENGTH = 10;

export type BreadcrumbEntityType = 'bill' | 'legislator' | 'committee';

export interface BreadcrumbEntry {
  entityType: BreadcrumbEntityType;
  entityId: number;
  label: string;
  path: string;
}

interface ResearchBreadcrumbContextType {
  trail: BreadcrumbEntry[];
  pushBreadcrumb: (entry: BreadcrumbEntry) => void;
  popTo: (index: number) => void;
  clearTrail: () => void;
}

const ResearchBreadcrumbContext = createContext<ResearchBreadcrumbContextType | null>(null);

function loadTrail(): BreadcrumbEntry[] {
  return session.get<BreadcrumbEntry[]>(STORAGE_KEY) ?? [];
}

function saveTrail(trail: BreadcrumbEntry[]) {
  session.set(STORAGE_KEY, trail);
}

export function ResearchBreadcrumbProvider({ children }: { children: ReactNode }) {
  const [trail, setTrail] = useState<BreadcrumbEntry[]>(loadTrail);
  const navigate = useNavigate();

  const pushBreadcrumb = useCallback((entry: BreadcrumbEntry) => {
    setTrail((prev) => {
      const filtered = prev.filter(
        (e) => !(e.entityType === entry.entityType && e.entityId === entry.entityId)
      );
      const next = [...filtered, entry].slice(-MAX_TRAIL_LENGTH);
      saveTrail(next);
      return next;
    });
  }, []);

  const popTo = useCallback(
    (index: number) => {
      setTrail((prev) => {
        if (index < 0 || index >= prev.length) return prev;
        const next = prev.slice(0, index + 1);
        saveTrail(next);
        navigate(prev[index].path);
        return next;
      });
    },
    [navigate]
  );

  const clearTrail = useCallback(() => {
    setTrail([]);
    session.remove(STORAGE_KEY);
  }, []);

  return (
    <ResearchBreadcrumbContext.Provider value={{ trail, pushBreadcrumb, popTo, clearTrail }}>
      {children}
    </ResearchBreadcrumbContext.Provider>
  );
}

export function useResearchBreadcrumbs(): ResearchBreadcrumbContextType {
  const context = useContext(ResearchBreadcrumbContext);
  if (!context) {
    throw new Error('useResearchBreadcrumbs must be used within a ResearchBreadcrumbProvider');
  }
  return context;
}
